/**
 * The betting vocabulary, as data.
 *
 * One entry per word a reader has to know to use this site, and the single source every teaching
 * surface reads from — the inline gloss, the panel behind it, and anything later.
 *
 * WHAT A GOOD ENTRY LOOKS LIKE. `gloss` is the load-bearing field: it renders *inline*, in the
 * sentence, because that is what the evidence supports. Chen (2016) compared gloss modes head to
 * head and pop-ups scored lowest on every test, the mechanism being split attention; NN/g reaches
 * the same conclusion and calls the winner paired terminology — "stake (how much you risk)", right
 * where the word is used. So a gloss must read mid-sentence: a lowercase fragment, no trailing full
 * stop, short enough not to derail the line it interrupts.
 *
 * `definition` is for a reader who tapped the word and wants more; assume they asked. `why` is the
 * field this domain needs and a card glossary would not: these words carry consequences for money,
 * and knowing what "de-vig" means is useless without knowing why skipping it costs you.
 */

export type TermId =
  | 'stake'
  | 'odds'
  | 'edge'
  | 'devig'
  | 'margin'
  | 'sharp-book'
  | 'break-even'
  | 'kelly'
  | 'closing-line'
  | 'series-market'
  | 'map-market'
  | 'pre-draft'
  | 'post-draft'
  | 'calibration'
  | 'brier'
  | 'resolution'
  | 'gold'
  | 'bankroll'
  | 'void-bet'
  | 'awaiting-result'
  | 'not-priced'
  | 'stake-cap'
  | 'bot-chalk'
  | 'bot-romantic'
  | 'bot-draft-master'
  | 'bot-follower'

export interface TermEntry {
  id: TermId
  /** The word as the UI writes it, and the default text `<Term>` renders. */
  term: string
  /** The inline parenthetical. Lowercase fragment, no trailing period. */
  gloss: string
  /** The panel behind the word. One to three sentences. */
  definition: string
  /** Why it matters to someone about to risk money. Optional, but usually the point. */
  why?: string
}

export const GLOSSARY: readonly TermEntry[] = [
  {
    id: 'stake',
    term: 'Stake',
    gloss: 'how much of your bankroll to risk',
    definition:
      'The share of your total betting money to put on one bet, shown as a percentage rather than ' +
      'a cash amount so it means the same thing whatever your bankroll is. 2% of a 1,000 kr bankroll ' +
      'is 20 kr.',
    why:
      'Sizing is what separates surviving a losing run from being wiped out by one. This site never ' +
      'suggests more than 2% on a single bet, however good the edge looks.',
  },
  {
    id: 'odds',
    term: 'Odds',
    gloss: 'the price the bookmaker is offering, not a recommendation',
    definition:
      'The decimal price actually on offer at the bookmaker — what you are paid per unit staked, ' +
      'including your stake back. 2.50 means a 100 kr bet returns 250 kr if it wins.',
    why:
      'This is the observed market price, captured before the match. It is not the price we think ' +
      'you should need — that is the break-even price, and the two are different numbers.',
  },
  {
    id: 'edge',
    term: 'Edge',
    gloss: 'how far our estimate sits above the price on offer, in percentage points',
    definition:
      'Our probability minus the one the price implies. At 1.31 a bookmaker is offering you 76%, so ' +
      'an estimate of 79% is a three-point edge. The margin is left in, because the price is what a ' +
      'winning stake is actually paid at.',
    why:
      'Edge is an estimate of an estimate. A few points is inside the noise of both the model and ' +
      'the line, which is why a bet has to clear a bar before it is suggested — and the price that ' +
      'clears it is the one shown as "worth backing at". They are the same sum, so the two numbers ' +
      'can never disagree. Note that both sides of a market can show a negative edge at once: the ' +
      "gap between them is the bookmaker's cut.",
  },
  {
    id: 'devig',
    term: 'De-vig',
    gloss: "stripping out the bookmaker's built-in cut",
    definition:
      'A bookmaker prices both sides so the implied chances add up to more than 100%. The excess is ' +
      'their commission. De-vigging rescales both sides back to 100% so what remains is their ' +
      'actual opinion.',
    why:
      'It answers what a bookmaker THINKS, which is a different question from whether a price pays ' +
      'you — the commission is still charged whatever their opinion. So it is how one book is ' +
      'compared against another, and it is not what an edge is measured from here: a bet judged ' +
      'that way could be advised at a price that loses money, and at a 10% book most of them would ' +
      'be.',
  },
  {
    id: 'margin',
    term: 'Margin',
    gloss: "the bookmaker's cut, built into both prices",
    definition:
      'How much more than 100% the two sides add up to. A 7% margin means the book has priced in a ' +
      '7% advantage for itself before anyone is right about anything.',
    why:
      'It is a toll you pay on every bet. At 7%, a genuine coin flip needs to win 53.5% of the time ' +
      'just to break even.',
  },
  {
    id: 'sharp-book',
    term: 'Sharp book',
    gloss: 'a bookmaker whose price is closest to the truth',
    definition:
      'A bookmaker — Pinnacle here — that runs a thin margin, takes large bets, and welcomes ' +
      'skilled bettors, because informed money is how it finds the right price. Its line is the ' +
      'closest thing to a market consensus that exists.',
    why:
      'It is a reference, not an opponent. When our model disagrees with a soft bookmaker, the ' +
      'sharp price says whether that soft price is genuinely too long — or whether we are the one ' +
      'who is wrong. It is usually us.',
  },
  {
    id: 'break-even',
    term: 'Break-even price',
    gloss: 'the price at which a bet stops losing money on average',
    definition:
      'One divided by our probability. If we make a team 62% likely to win, the break-even price is ' +
      '1 ÷ 0.62 = 1.61.',
    why:
      'Taking exactly this price is not a good bet — it is zero expected profit before variance, ' +
      'and the bookmaker keeps the difference. You want to be paid meaningfully above it.',
  },
  {
    id: 'kelly',
    term: 'Kelly',
    gloss: 'a staking formula that scales with your edge',
    definition:
      'A rule that sizes a bet in proportion to how large the edge is and how long the price is. ' +
      'This site uses quarter-Kelly: a quarter of what the formula says.',
    why:
      'Full Kelly is only optimal if your probabilities are exactly right. They never are, and it ' +
      'punishes an overestimated edge brutally — so a quarter trades a little growth for far less ' +
      'chance of ruin.',
  },
  {
    id: 'closing-line',
    term: 'Closing line',
    gloss: 'the final price before a match starts',
    definition:
      'The last price a bookmaker offers before betting closes, after every other bettor has had ' +
      'their say. It is the market at its best informed.',
    why:
      'Beating the closing line consistently is the strongest evidence an edge is real — far ' +
      'stronger than profit, which takes hundreds of bets to separate from luck.',
  },
  {
    id: 'series-market',
    term: 'Series',
    gloss: 'a bet on who wins the whole match, not one game',
    definition:
      'Who takes the best-of-three or best-of-five overall. A Bo3 is won by the first team to two ' +
      'maps.',
    why:
      'A series gives the stronger team more chances, so it magnifies an edge — and magnifies an ' +
      'error by exactly as much. Our model estimates single maps; the series number is derived.',
  },
  {
    id: 'map-market',
    term: 'Map',
    gloss: 'a bet on one individual game',
    definition: 'A single game within a series. "Map 1" is the first game specifically.',
    why:
      'This is what the model actually predicts, so a map price is the more honest comparison. A ' +
      'thin map edge can look convincing once converted to a series price.',
  },
  {
    id: 'pre-draft',
    term: 'Pre-draft',
    gloss: 'estimated before the heroes are picked',
    definition:
      'A prediction made from team strength alone, before or during hero selection. Available while ' +
      'the betting market is still open.',
    why:
      'This is the number you can act on, because the market closes when the game starts.',
  },
  {
    id: 'post-draft',
    term: 'Post-draft',
    gloss: 'estimated once all ten heroes are locked',
    definition:
      'A prediction made once all ten heroes are locked, with the draft fed to the model. Shown ' +
      'only where we actually saw the heroes before the horn — never the pre-draft number wearing ' +
      'a different label.',
    why:
      'It is the sharper of the two numbers, and the bookmaker has usually not repriced between ' +
      'them: the line in front of you was set before anyone could see the draft. That gap is the ' +
      'opportunity this site exists to catch.',
  },
  {
    id: 'calibration',
    term: 'Calibration',
    gloss: 'whether a stated percentage matches how often it happens',
    definition:
      'A model is calibrated when the things it calls 70% happen about 70% of the time. It is ' +
      'separate from accuracy: a model can pick winners well and still state the wrong numbers.',
    why:
      'For betting, calibration matters more than accuracy, because the price you are willing to ' +
      'accept is set by the number, not by whether the favourite won.',
  },
  {
    id: 'brier',
    term: 'Brier score',
    gloss: 'a score for probabilities, where lower is better',
    definition:
      'The average squared distance between a stated probability and what happened — 0 is perfect, ' +
      '0.25 is what saying 50% to everything gets you. It is a proper score, meaning a forecaster ' +
      'cannot improve it by stating anything other than what it actually believes.',
    why:
      'It is the fair way to compare our numbers against a bookmaker’s on the same matches. ' +
      'Counting who named more winners is not: a forecaster that says 51% about everything can win ' +
      'that contest while being useless to bet with.',
  },
  {
    id: 'resolution',
    term: 'Resolution',
    gloss: 'how far a forecaster commits from the average, and is right to',
    definition:
      'How much better a forecaster does than always saying the base rate, once any error in its ' +
      'stated percentages is corrected for. It is the information half of a forecast — the other ' +
      'half is calibration, which is whether the percentages are labelled correctly.',
    why:
      'The two have unrelated cures. Bad calibration can be fixed by relabelling the numbers; low ' +
      'resolution cannot be fixed at all without knowing more about the match. Our gap to the sharp ' +
      'market is the second kind, which is why no recalibration would close it.',
  },
  {
    id: 'gold',
    term: 'Gold',
    gloss: 'the fantasy currency, same as in the game',
    definition:
      'What you bet with. Everyone starts a season with 10,000 gold, whenever they join, and there ' +
      'is no way to get more — no rebuys, no top-ups.',
    why:
      'It is play money and there is no prize, so the only thing it buys is bragging rights. That ' +
      'is also why nothing is taken off the top: the odds are the model’s own numbers.',
  },
  {
    id: 'bankroll',
    term: 'Bankroll',
    gloss: 'the gold you have left',
    definition:
      'What you started the season with, plus everything settled bets have won or lost. Gold ' +
      'riding on bets that have not finished is shown separately rather than taken off this — ' +
      'otherwise placing a bet would look like losing one.',
    why:
      'The leaderboard ranks on this. There is no cap on a single bet, so a big call can move you ' +
      'a long way in either direction.',
  },
  {
    id: 'void-bet',
    term: 'Void',
    gloss: 'the map was never played, so the stake came back',
    definition:
      'A bet that can never be decided. A best-of-three that ends 2-0 never plays its third map, ' +
      'and a lobby that never became a game is the same thing for a different reason. The stake is ' +
      'returned in full.',
    why:
      'It is not a loss and not still running. Counting it as a loss would invent one you never ' +
      'had; leaving it open would freeze part of your bankroll for good.',
  },
  {
    id: 'awaiting-result',
    term: 'Result pending',
    gloss: 'the map was played; the result has not reached us yet',
    definition:
      'Results come from OpenDota, which indexes matches some way behind real time — during a ' +
      'tournament that can be an hour or more. The map has a lobby and a kick-off in the past, so ' +
      'it was played; we simply do not have the winner yet. It fills itself in.',
    why:
      'This used to read "Not started", which is a different thing and was plainly wrong beside a ' +
      'later game already in progress. Nothing is stuck, and no bet on it is at risk of being ' +
      'settled wrongly — an unsettled bet waits for the result rather than guessing at it.',
  },
  {
    id: 'not-priced',
    term: 'not priced by this book',
    gloss: 'the bookmaker we quote never offered this market',
    definition:
      'Every row on this card is the same bookmaker, so the numbers can be compared with each ' +
      'other. Where that book never put a price up, the row says so instead of quietly ' +
      'disappearing. A third map is the usual case: a book often opens it only once a ' +
      'best-of-three is level and the map is actually going to be played.',
    why:
      'Other bookmakers may well have priced it. Substituting one of them here would put two ' +
      'books in one column with nothing saying which is which, and the comparison between the ' +
      'rows would stop meaning anything. A missing row and an unpriced market are different ' +
      'facts, and this says which one you are looking at.',
  },
  {
    id: 'stake-cap',
    term: 'stake cap',
    gloss: 'the most you may put on one bet, and on one match',
    definition:
      'One bet can risk up to a tenth of the gold the season started you with, and everything you ' +
      'have open on a single match adds up to at most a quarter of it. Type more and the stake is ' +
      'reduced to the limit rather than refused.',
    why:
      'There are no rebuys, so going all in does not risk your gold — it risks your season, and a ' +
      'week of matches with nothing left to bet on. The match limit exists because the series and ' +
      'its maps are close to the same bet: backing one side six times is going all in with extra ' +
      'steps. The house characters stake around a hundredth of theirs, so this also keeps the ' +
      'leaderboard a contest between reads rather than between exposures.',
  },
  {
    id: 'bot-chalk',
    term: 'Populist',
    gloss: 'always backs the favourite, same stake every time',
    definition:
      'A house bot. It puts a fixed 100 units on whichever side the model makes favourite, on ' +
      'every match it can.',
    why:
      'Expected to do well, which is the fun part: the model is under-confident, so backing its ' +
      'own favourites at its own odds should turn a profit. If the Populist tops the table, the ' +
      'site has published proof of its own bias.',
  },
  {
    id: 'bot-romantic',
    term: 'The (under)dog',
    gloss: 'always backs the outsider, same stake every time',
    definition:
      'A house bot, and the control. It puts a fixed 100 units on whichever side the model makes ' +
      'the outsider, every time.',
    why:
      'Its record is what pure luck looks like against these odds — which is the only thing that ' +
      'makes anybody else’s record mean something.',
  },
  {
    id: 'bot-draft-master',
    term: 'Draft Master',
    gloss: 'waits for all ten heroes, then bets on odds that ignore them',
    definition:
      'A house bot. It uses the model’s post-draft read — fifty features against thirty-seven — ' +
      'and bets when that disagrees with the posted odds by at least three points, sizing by ' +
      'Kelly rather than flat.',
    why:
      'It should win, and its profit is a measure of what reading a draft is worth. You can do ' +
      'exactly the same thing: the odds never move, so the heroes are free information right up ' +
      'to the horn.',
  },
  {
    id: 'bot-follower',
    term: 'Market Man',
    gloss: 'backs whoever the real bookmakers like',
    definition:
      'A house bot. Where a real bookmaker price was captured for a match, it strips out that ' +
      'book’s margin and backs their favourite.',
    why:
      'It asks whether the model can be beaten by the wider market. It bets far less often than ' +
      'the others, because a real price costs one of 250 monthly calls — which is why the ' +
      'leaderboard always shows a bet count.',
  },
]

const BY_ID = new Map(GLOSSARY.map((entry) => [entry.id, entry]))

export function term(id: TermId): TermEntry {
  const entry = BY_ID.get(id)
  if (!entry) {
    // Loud rather than silent: a missing entry means a word is being used untaught, and a
    // placeholder that renders fine would hide that indefinitely.
    throw new Error(`No glossary entry for "${id}".`)
  }
  return entry
}
